
import React from 'react';
import DashboardLanding from '@/components/dashboard/DashboardLanding';
import FeatureErrorBoundary from '@/components/FeatureErrorBoundary';
import DashboardErrorState from '@/components/dashboard/errors/DashboardErrorState';
import { type EnhancedCrawlResult, type CrawlProgress } from '@/types/crawler';
import { DashboardWidgetRefs, RetryHandlers } from '@/components/dashboard/types';
import LoadingContent from './loading/LoadingContent';
import ResultsContent from './results/ResultsContent';

interface DashboardContentProps {
  isAnalyzing: boolean;
  progress: CrawlProgress;
  crawlResult: EnhancedCrawlResult | null;
  websiteUrl: string;
  activePage: string;
  selectedPageUrl: string | null;
  onSubmit: (url: string) => void;
  onCancel: () => void;
  onNewCrawl: () => void;
  onNavigate: (page: string) => void;
  onSelectPage: (url: string) => void;
  widgetRefs: DashboardWidgetRefs;
  retryHandlers: RetryHandlers;
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  isAnalyzing,
  progress,
  crawlResult,
  websiteUrl,
  activePage,
  selectedPageUrl,
  onSubmit,
  onCancel,
  onNewCrawl,
  onNavigate,
  onSelectPage,
  widgetRefs,
  retryHandlers
}) => {
  // Show landing page when there's no crawl in progress or results
  if (!isAnalyzing && !crawlResult) {
    return (
      <div data-testid="dashboard-landing-container">
        <DashboardLanding
          onSubmit={onSubmit}
          isLoading={isAnalyzing}
        />
      </div>
    );
  }

  // Show loading state during crawl
  if (isAnalyzing) {
    return (
      <LoadingContent 
        progress={progress}
        onCancel={onCancel}
        websiteUrl={websiteUrl}
      />
    );
  }

  // Show results and widgets when crawl is complete
  if (crawlResult) {
    return (
      <ResultsContent 
        crawlResult={crawlResult}
        onNewCrawl={onNewCrawl}
        onNavigate={onNavigate}
        selectedPageUrl={selectedPageUrl}
        widgetRefs={widgetRefs}
        onSelectPage={onSelectPage}
        retryHandlers={retryHandlers}
      />
    );
  }

  // Fallback for unexpected state
  return (
    <div data-testid="dashboard-error-container">
      <DashboardErrorState 
        message="There was an unexpected error loading the dashboard." 
        onRetry={() => window.location.reload()} 
      />
    </div>
  );
};

export default DashboardContent;
